import { useState } from 'react';
import { ArrowLeft, Upload, BookOpen, Pencil, Check, X, Trash2, Download, FileText, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { CaseStatusBadge } from '../cases/CaseStatusBadge';
import type { Case } from '../../types';

interface CaseTopBarProps {
  caseData: Case;
  onBack: () => void;
  onUploadDocuments: () => void;
  onOpenCaseDict?: () => void;
  onRenameCase?: (newName: string) => void;
  onDeleteCase?: () => void;
  onDownloadCase?: () => void;
  isDownloadDisabled?: boolean;
  isDownloading?: boolean;
  onExportMegaPdf?: () => void;
  isExporting?: boolean;
}

export function CaseTopBar({
  caseData,
  onBack,
  onUploadDocuments,
  onOpenCaseDict,
  onRenameCase,
  onDeleteCase,
  onDownloadCase,
  isDownloadDisabled,
  isDownloading,
  onExportMegaPdf,
  isExporting,
}: CaseTopBarProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(caseData.name);

  const handleStartEdit = () => {
    setEditName(caseData.name);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditName(caseData.name);
    setIsEditing(false);
  };

  const handleSave = () => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    if (trimmed !== caseData.name) {
      onRenameCase?.(trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="h-14 border-b border-border bg-white flex items-center justify-between px-4">
      {/* Left Section */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
          aria-label="Back to cases"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>

        <div className="flex items-center gap-3">
          {isEditing ? (
            <div className="flex items-center gap-1.5">
              <input
                autoFocus
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={500}
                className="font-medium text-sm border border-gray-300 rounded px-2 py-1 outline-none focus:border-blue-500 max-w-[400px]"
              />
              <button
                onClick={handleSave}
                disabled={!editName.trim()}
                className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                aria-label="Save name"
              >
                <Check className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={handleCancel}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                aria-label="Cancel editing"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-sm truncate max-w-[400px]" title={caseData.name}>{caseData.name}</span>
              {onRenameCase && (
                <button
                  onClick={handleStartEdit}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  aria-label="Rename case"
                >
                  <Pencil className="h-3.5 w-3.5 text-gray-400" />
                </button>
              )}
            </div>
          )}
          <CaseStatusBadge status={caseData.status} />
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-3">
        {onOpenCaseDict && (
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenCaseDict}
          >
            <BookOpen className="h-4 w-4 mr-1.5" />
            Case Dict
          </Button>
        )}
        {onDownloadCase && (
          <Button
            variant="outline"
            size="sm"
            onClick={onDownloadCase}
            disabled={isDownloadDisabled || isDownloading}
            title={isDownloadDisabled ? 'No finalized documents to download' : 'Download all redacted files as ZIP'}
          >
            {isDownloading ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1.5" />
            )}
            {isDownloading ? 'Downloading...' : 'Download ZIP'}
          </Button>
        )}
        {onExportMegaPdf && (
          <Button
            variant="outline"
            size="sm"
            onClick={onExportMegaPdf}
            disabled={isExporting}
            title="Export all documents as a single merged PDF with bookmarks and separators"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-1.5" />
            )}
            {isExporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        )}
        {onDeleteCase && (
          <Button
            variant="outline"
            size="sm"
            onClick={onDeleteCase}
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Delete All Files
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={onUploadDocuments}
        >
          <Upload className="h-4 w-4 mr-1.5" />
          Upload Documents
        </Button>
      </div>
    </div>
  );
}
